import React, { useState, useEffect } from 'react';
import IconPicker from './IconPicker';

const DecoderAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}` };

  // Decoder admin state
  const [decoderSettings, setDecoderSettings] = useState({ is_visible: true, is_pro_only: false, ai_enabled: true, max_input_length: 500, max_ai_tokens: 4096, rate_limit_per_hour: 20, rate_limit_per_day: 30, groq_monthly_limit: 1000, max_history_per_user: 50, max_vocabulary_per_user: 100, disclaimer_visible: true, disclaimer_text: '', beta_badge_visible: true, beta_badge_text: 'Beta / Experimental' });
  const [decoderReports, setDecoderReports] = useState([]);
  const [decoderCorrectionText, setDecoderCorrectionText] = useState('');
  const [decoderSubTab, setDecoderSubTab] = useState('settings');
  const [decoderUsageStats, setDecoderUsageStats] = useState({ daily: [], total_month: 0, groq_limit: 1000, top_users: [] });
  const [decoderUsageLoading, setDecoderUsageLoading] = useState(false);
  const [decoderSearchHistory, setDecoderSearchHistory] = useState([]);
  const [decoderSearchFilter, setDecoderSearchFilter] = useState('');
  const [decoderHistoryPage, setDecoderHistoryPage] = useState(1);
  const [decoderHistoryTotal, setDecoderHistoryTotal] = useState(0);
  const [decoderHistoryPages, setDecoderHistoryPages] = useState(1);
  const [decoderKnowledge, setDecoderKnowledge] = useState([]);
  const [decoderKnowledgeFilter, setDecoderKnowledgeFilter] = useState('');
  const [decoderKnowledgeStatusFilter, setDecoderKnowledgeStatusFilter] = useState('all');
  const [decoderKnowledgePage, setDecoderKnowledgePage] = useState(1);
  const [decoderKnowledgeTotal, setDecoderKnowledgeTotal] = useState(0);
  const [decoderKnowledgeVerified, setDecoderKnowledgeVerified] = useState(0);
  const [decoderKnowledgeUnverified, setDecoderKnowledgeUnverified] = useState(0);
  const [decoderKnowledgePages, setDecoderKnowledgePages] = useState(1);
  const [editingKnowledgeEntry, setEditingKnowledgeEntry] = useState(null);
  // Decoder DeepSeek state
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [iconPickerTarget, setIconPickerTarget] = useState('page'); // 'page', 'tab_activity', 'tab_vocabulary', 'tab_help', or help section id
  const [decoderDsKey, setDecoderDsKey] = useState({ set: false, masked: '', showInput: false, newKey: '', saving: false });
  const [decoderDsPrompt, setDecoderDsPrompt] = useState({ persona: '', grammar: '', examples: [], temperature: 0.2, showEditor: false, saving: false });
  const [decoderDsTest, setDecoderDsTest] = useState({ text: '', loading: false, result: null });
  // Decoder Help Content CRUD state
  const [decoderHelpSections, setDecoderHelpSections] = useState([]);
  const [editingHelpSection, setEditingHelpSection] = useState(null);
  const [newHelpSection, setNewHelpSection] = useState({ title: '', content: '', icon: '📝', icon_url: '', order: 0, is_active: true });

  const openIconPicker = async (target) => {
    setIconPickerTarget(target);
    setIconPickerOpen(true);
    setIconSearchQuery('');
    setAvailableIcons([]);
    try {
      const r = await fetch(`${API}/icons?t=${Date.now()}`, { headers: authHeaders, cache: 'no-store' });
      if (r.ok) { const d = await r.json(); setAvailableIcons(d.icons || []); }
    } catch {}
  };

const fetchDecoderHistory = async (page = 1, search = '') => {
  try {
    const params = new URLSearchParams({ page, per_page: 10, search });
    const res = await fetch(`${API}/admin/decoder/history?${params}`, { headers: authHeaders });
    if (res.ok) {
      const d = await res.json();
      const historyArr = d.history || (Array.isArray(d) ? d : []);
      setDecoderSearchHistory(historyArr);
      setDecoderHistoryTotal(d.total != null ? d.total : historyArr.length);
      setDecoderHistoryPages(d.pages || 1);
      setDecoderHistoryPage(d.page || 1);
    }
  } catch (e) { console.error('Failed to load decoder history:', e); }
};

const fetchDecoderUsageStats = async () => {
  setDecoderUsageLoading(true);
  try {
    const res = await fetch(`${API}/admin/decoder/usage-stats`, { headers: authHeaders });
    if (res.ok) {
      const d = await res.json();
      setDecoderUsageStats(d);
    }
  } catch (e) { console.error('Failed to load decoder usage stats:', e); }
  finally { setDecoderUsageLoading(false); }
};

const fetchDecoderKnowledge = async (page = 1, status = 'all', search = '') => {
  try {
    const params = new URLSearchParams({ page, per_page: 10, status, search });
    const res = await fetch(`${API}/admin/decoder/knowledge?${params}`, { headers: authHeaders });
    if (res.ok) {
      const d = await res.json();
      const knowledgeArr = d.knowledge || (Array.isArray(d) ? d : []);
      setDecoderKnowledge(knowledgeArr);
      setDecoderKnowledgeTotal(d.total != null ? d.total : knowledgeArr.length);
      setDecoderKnowledgeVerified(d.total_verified != null ? d.total_verified : knowledgeArr.filter(k => k.is_verified).length);
      setDecoderKnowledgeUnverified(d.total_unverified != null ? d.total_unverified : knowledgeArr.filter(k => !k.is_verified).length);
      setDecoderKnowledgePages(d.pages || 1);
      setDecoderKnowledgePage(d.page || 1);
    }
  } catch (e) { console.error('Failed to load decoder knowledge:', e); }
};

  // Load all decoder data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [decSetRes, decRepRes, decHelpRes] = await Promise.all([
        fetch(`${API}/admin/decoder/settings`, { headers: authHeaders }),
        fetch(`${API}/admin/decoder/reports`, { headers: authHeaders }),
        fetch(`${API}/admin/decoder/help`, { headers: authHeaders })
      ]);
      if (decSetRes.ok) {
        const ds = await decSetRes.json();
        setDecoderSettings(ds);
        setDecoderDsKey({ set: !!ds.deepseek_api_key_set, masked: ds.deepseek_api_key_masked || '', showInput: false, newKey: '', saving: false });
        setDecoderDsPrompt(prev => ({ ...prev, persona: ds.ds_persona || ds.ds_persona_default || '', grammar: ds.ds_grammar || ds.ds_grammar_default || '', examples: ds.ds_examples || ds.ds_examples_default || [], temperature: ds.ds_temperature ?? 0.2 }));
      }
      if (decRepRes.ok) { const dr = await decRepRes.json(); setDecoderReports(dr.reports || []); }
      if (decHelpRes.ok) { const dh = await decHelpRes.json(); setDecoderHelpSections(dh.sections || dh || []); }
    } catch (e) { console.error('Failed to load decoder admin data:', e); }
    // Load paginated data
    await fetchDecoderHistory(1, '');
    await fetchDecoderKnowledge(1, 'all', '');
  };

  return (
    <>
                <div className="space-y-6">
                  {/* Active Provider Banner */}
                  <div className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${
                    (decoderSettings.ai_provider || 'groq') === 'deepseek'
                      ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-blue-500'
                      : 'bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border-purple-500'
                  }`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2.5 h-2.5 rounded-full animate-pulse ${
                        (decoderSettings.ai_provider || 'groq') === 'deepseek' ? 'bg-blue-400' : 'bg-purple-400'
                      }`}></span>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        Active Provider: {(decoderSettings.ai_provider || 'groq') === 'deepseek' ? 'DeepSeek' : 'Groq (Llama)'}
                      </span>
                    </div>
                    <button onClick={() => setDecoderSubTab('settings')}
                      className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                    >Switch</button>
                  </div>
                  {/* Sub-Tab Navigation */}
                  <div className={`flex flex-wrap gap-2 p-1 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    {[
                      { key: 'settings', label: 'Settings', icon: '⚙️' },
                      { key: 'deepseek', label: 'DeepSeek AI', icon: '🤖' },
                      { key: 'usage', label: 'Usage Stats', icon: '📊' },
                      { key: 'history', label: `Search History (${decoderHistoryTotal})`, icon: '🔍' },
                      { key: 'reports', label: `Reports (${decoderReports.filter(r => r.status === 'pending').length})`, icon: '📨' },
                      { key: 'knowledge', label: `Knowledge Base (${decoderKnowledgeVerified + decoderKnowledgeUnverified})`, icon: '🧠' },
                      { key: 'help', label: 'Help Content', icon: '📖' }
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => { setDecoderSubTab(tab.key); if (tab.key === 'usage') fetchDecoderUsageStats(); }}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                          decoderSubTab === tab.key
                            ? isDarkMode ? 'bg-blue-600 text-white shadow-md' : 'bg-white text-blue-700 shadow-md'
                            : isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span>{tab.icon}</span> {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Settings Sub-Tab */}
                  {decoderSubTab === 'settings' && (<>
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🔮 Darija Decoder Settings
                    </h4>
                    <div className="space-y-4">
                      {/* Visible Toggle */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Show in Menu</p>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Show/hide Darija Decoder in sidebar navigation</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !decoderSettings.is_visible;
                            setDecoderSettings(prev => ({...prev, is_visible: newVal}));
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...decoderSettings, is_visible: newVal})
                              });
                              showNotification(newVal ? 'Decoder visible' : 'Decoder hidden', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${decoderSettings.is_visible ? 'bg-green-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${decoderSettings.is_visible ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {/* Pro Only Toggle */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>💎 Premium Only</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Require Pro subscription to use Decoder</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !decoderSettings.is_pro_only;
                            setDecoderSettings(prev => ({...prev, is_pro_only: newVal}));
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...decoderSettings, is_pro_only: newVal})
                              });
                              showNotification(newVal ? 'Decoder: Pro only' : 'Decoder: Free for all', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${decoderSettings.is_pro_only ? 'bg-purple-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${decoderSettings.is_pro_only ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {/* Groq API Key */}
                      <div className={`p-3 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Groq API Key</p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Required for AI decoding. Get one free at console.groq.com</p>
                          </div>
                          {decoderSettings.groq_api_key_set ? (
                            <span className="text-green-500 text-sm font-medium">Configured ({decoderSettings.groq_api_key_masked})</span>
                          ) : (
                            <span className="text-red-400 text-sm">Not configured</span>
                          )}
                        </div>
                        {decoderSettings._showKeyInput ? (
                          <div className="space-y-2">
                            <textarea
                              value={decoderSettings._newApiKey || ''}
                              onChange={(e) => setDecoderSettings(prev => ({...prev, _newApiKey: e.target.value}))}
                              placeholder="Paste your Groq API key here (gsk_...)"
                              rows={2}
                              className={`w-full p-2 rounded text-sm font-mono ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'} border`}
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  const key = decoderSettings._newApiKey?.trim();
                                  if (!key) return;
                                  try {
                                    await fetch(`${API}/admin/decoder/settings`, {
                                      method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                      body: JSON.stringify({ groq_api_key: key })
                                    });
                                    setDecoderSettings(prev => ({
                                      ...prev,
                                      groq_api_key_set: true,
                                      groq_api_key_masked: `***${key.slice(-4)}`,
                                      _showKeyInput: false,
                                      _newApiKey: ''
                                    }));
                                    showNotification('Groq API key saved', 'success');
                                  } catch (e) { showNotification('Failed to save key', 'error'); }
                                }}
                                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                              >Save Key</button>
                              {decoderSettings.groq_api_key_set && (
                                <button
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API}/admin/decoder/settings`, {
                                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                        body: JSON.stringify({ groq_api_key: '' })
                                      });
                                      setDecoderSettings(prev => ({
                                        ...prev,
                                        groq_api_key_set: false,
                                        groq_api_key_masked: '',
                                        _showKeyInput: false,
                                        _newApiKey: ''
                                      }));
                                      showNotification('Groq API key removed', 'success');
                                    } catch (e) { showNotification('Failed to remove key', 'error'); }
                                  }}
                                  className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
                                >Remove</button>
                              )}
                              <button
                                onClick={() => setDecoderSettings(prev => ({...prev, _showKeyInput: false, _newApiKey: ''}))}
                                className={`px-3 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'}`}
                              >Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDecoderSettings(prev => ({...prev, _showKeyInput: true}))}
                            className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >{decoderSettings.groq_api_key_set ? 'Change Key' : 'Add Key'}</button>
                        )}
                      </div>
                      {/* Active AI Provider */}
                      <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                        <p className={`font-medium text-sm mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Active AI Provider</p>
                        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Choose which AI provider handles decoder requests. Configure DeepSeek in the DeepSeek AI tab.</p>
                        <div className="flex gap-2">
                          {[{id:'groq', label:'Groq (Llama)', color:'purple'}, {id:'deepseek', label:'DeepSeek', color:'blue'}].map(p => (
                            <button key={p.id}
                              onClick={async () => {
                                if (p.id === 'deepseek' && !decoderDsKey.set) {
                                  showNotification('Add DeepSeek API key first in the DeepSeek AI tab', 'error');
                                  return;
                                }
                                setDecoderSettings(prev => ({...prev, ai_provider: p.id}));
                                try {
                                  await fetch(`${API}/admin/decoder/settings`, {
                                    method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                    body: JSON.stringify({ ai_provider: p.id })
                                  });
                                  showNotification(`Switched to ${p.label}`, 'success');
                                } catch(e) { showNotification('Failed to switch provider', 'error'); }
                              }}
                              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all border-2 ${
                                (decoderSettings.ai_provider || 'groq') === p.id
                                  ? p.color === 'purple'
                                    ? 'bg-purple-600 text-white border-purple-600 shadow-lg'
                                    : 'bg-blue-600 text-white border-blue-600 shadow-lg'
                                  : isDarkMode ? 'bg-gray-700 text-gray-300 border-gray-600 hover:border-gray-500' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
                              }`}
                            >
                              {(decoderSettings.ai_provider || 'groq') === p.id ? '● ' : '○ '}{p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* AI Translation Toggle */}
                      <div className={`flex items-center justify-between p-3 rounded-lg ${
                        decoderSettings.ai_enabled
                          ? 'bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30'
                          : 'bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30'
                      }`}>
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {decoderSettings.ai_enabled ? '\u{1F916}' : '\u{1F4D6}'} AI Translation (Groq)
                          </p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {decoderSettings.ai_enabled
                              ? 'AI enabled — unknown phrases are translated by Groq AI and cached'
                              : 'AI disabled — only phrases from the knowledge base will be returned'}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !decoderSettings.ai_enabled;
                            setDecoderSettings(prev => ({...prev, ai_enabled: newVal}));
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...decoderSettings, ai_enabled: newVal})
                              });
                              showNotification(newVal ? 'AI translation enabled' : 'AI translation disabled — database only mode', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${decoderSettings.ai_enabled ? 'bg-blue-600' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${decoderSettings.ai_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {!decoderSettings.ai_enabled && (
                        <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-amber-900/30 border border-amber-700 text-amber-300' : 'bg-amber-50 border border-amber-300 text-amber-800'}`}>
                          Only translations from the knowledge base will be returned. New or unknown phrases will show a "not available" message to users.
                        </div>
                      )}
                      {/* Max Input Characters */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Max Input Characters</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Maximum characters a user can type in the decoder input</p>
                        </div>
                        <input
                          type="number" min="50" max="5000"
                          value={decoderSettings.max_input_length || 500}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 50 && val <= 5000) {
                              setDecoderSettings(prev => ({...prev, max_input_length: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, max_input_length: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-20 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Max AI Response Tokens */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Max AI Response Tokens</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Controls how long the AI response can be (higher = longer sentences work)</p>
                        </div>
                        <input
                          type="number" min="512" max="8192" step="512"
                          value={decoderSettings.max_ai_tokens || 4096}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 512 && val <= 8192) {
                              setDecoderSettings(prev => ({...prev, max_ai_tokens: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, max_ai_tokens: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-20 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Token Reference Guide */}
                      <div className="space-y-2">
                        <p className={`font-semibold text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Token Guide:</p>
                        <div className="grid grid-cols-5 gap-2">
                          {[
                            { tokens: '512', words: '~380', label: 'Short phrase', active: (decoderSettings.max_ai_tokens || 4096) === 512 },
                            { tokens: '1024', words: '~760', label: '3-5 words', active: (decoderSettings.max_ai_tokens || 4096) === 1024 },
                            { tokens: '2048', words: '~1.5K', label: '5-10 words', active: (decoderSettings.max_ai_tokens || 4096) === 2048 },
                            { tokens: '4096', words: '~3K', label: 'Default', active: (decoderSettings.max_ai_tokens || 4096) === 4096 },
                            { tokens: '8192', words: '~6K', label: 'Very long', active: (decoderSettings.max_ai_tokens || 4096) === 8192 },
                          ].map(t => (
                            <div key={t.tokens} className={`p-2 rounded-lg text-center text-xs border ${
                              t.active
                                ? isDarkMode ? 'bg-blue-600/30 border-blue-500 text-blue-300' : 'bg-blue-100 border-blue-400 text-blue-700'
                                : isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'
                            }`}>
                              <p className="font-bold">{t.tokens}</p>
                              <p className="opacity-70">{t.words}</p>
                              <p className="mt-0.5">{t.label}</p>
                            </div>
                          ))}
                        </div>
                        <p className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          4096 recommended. Typical decode uses 200-500 tokens. Higher = more Groq API cost (but cheap).
                        </p>
                      </div>
                      {/* Rate Limit */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Rate Limit (per hour)</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max decodes per user per hour</p>
                        </div>
                        <input
                          type="number" min="1" max="100"
                          value={decoderSettings.rate_limit_per_hour || 20}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 1 && val <= 100) {
                              setDecoderSettings(prev => ({...prev, rate_limit_per_hour: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, rate_limit_per_hour: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-16 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Rate Limit per Day */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Rate Limit (per day)</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max decodes per user per day. Protects Groq API quota.</p>
                        </div>
                        <input
                          type="number" min="1" max="500"
                          value={decoderSettings.rate_limit_per_day || 30}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 1 && val <= 500) {
                              setDecoderSettings(prev => ({...prev, rate_limit_per_day: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, rate_limit_per_day: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-16 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Groq Monthly Limit */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Groq Monthly Limit</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Your Groq API monthly quota (for usage tracking display)</p>
                        </div>
                        <input
                          type="number" min="100" max="100000"
                          value={decoderSettings.groq_monthly_limit || 1000}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 100 && val <= 100000) {
                              setDecoderSettings(prev => ({...prev, groq_monthly_limit: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, groq_monthly_limit: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-20 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Max History Per User */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Max History Per User</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Maximum recent decodes stored per user (controls database size)</p>
                        </div>
                        <input
                          type="number" min="5" max="200"
                          value={decoderSettings.max_history_per_user || 50}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 5 && val <= 200) {
                              setDecoderSettings(prev => ({...prev, max_history_per_user: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, max_history_per_user: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-16 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                      {/* Max Vocabulary Per User */}
                      <div className="flex items-center justify-between">
                        <div className="flex-1 mr-4">
                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Max Words Per User</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Maximum saved words in "My Words" per user</p>
                        </div>
                        <input
                          type="number" min="10" max="500"
                          value={decoderSettings.max_vocabulary_per_user || 100}
                          onChange={async (e) => {
                            const val = parseInt(e.target.value);
                            if (val >= 10 && val <= 500) {
                              setDecoderSettings(prev => ({...prev, max_vocabulary_per_user: val}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({...decoderSettings, max_vocabulary_per_user: val})
                                });
                              } catch (e) {}
                            }
                          }}
                          className={`w-16 px-2 py-1 border rounded text-center ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                      </div>
                    </div>
                  </div>
                  {/* Beta Badge - Separate Card */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-amber-800 bg-amber-900/30' : 'border-amber-200 bg-amber-50'}`}>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-amber-300' : 'text-amber-800'}`}>
                            🏷️ Beta Badge
                          </h4>
                          <p className={`text-sm ${isDarkMode ? 'text-amber-400/70' : 'text-amber-600'}`}>Show "Beta / Experimental" banner on top of decoder page</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !decoderSettings.beta_badge_visible;
                            setDecoderSettings(prev => ({...prev, beta_badge_visible: newVal}));
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...decoderSettings, beta_badge_visible: newVal})
                              });
                              showNotification(newVal ? 'Beta badge shown' : 'Beta badge hidden', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${decoderSettings.beta_badge_visible ? 'bg-amber-500' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${decoderSettings.beta_badge_visible ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {decoderSettings.beta_badge_visible && (
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>Badge Text</label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={decoderSettings.beta_badge_text || ''}
                              onChange={(e) => setDecoderSettings(prev => ({...prev, beta_badge_text: e.target.value}))}
                              placeholder="Beta / Experimental"
                              className={`flex-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-amber-900/50 border-amber-700 text-white placeholder-amber-500' : 'bg-white border-amber-300'}`}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  await fetch(`${API}/admin/decoder/settings`, {
                                    method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                    body: JSON.stringify({beta_badge_text: decoderSettings.beta_badge_text})
                                  });
                                  showNotification('Badge text saved', 'success');
                                } catch (e) { showNotification('Failed to save', 'error'); }
                              }}
                              className="px-4 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Page Icon - Separate Card */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-purple-800 bg-purple-900/30' : 'border-purple-200 bg-purple-50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-purple-300' : 'text-purple-800'}`}>
                          🎨 Page Icon
                        </h4>
                        <p className={`text-sm ${isDarkMode ? 'text-purple-400/70' : 'text-purple-600'}`}>Custom icon shown at the top of the Decoder page (replaces default 🔮)</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {decoderSettings.decoder_icon_url ? (
                          <>
                            <img src={`${API.replace('/api', '')}${decoderSettings.decoder_icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                            <button onClick={async () => {
                              setDecoderSettings(prev => ({...prev, decoder_icon_url: ''}));
                              try {
                                await fetch(`${API}/admin/decoder/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ decoder_icon_url: '' }) });
                                showNotification('Icon cleared', 'success');
                              } catch {}
                            }} className="text-red-400 text-xs hover:text-red-300">✕</button>
                          </>
                        ) : <span className="text-2xl">🔮</span>}
                        <button onClick={() => openIconPicker('page')} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                          Pick Icon
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Note Section - Separate Card */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-teal-800 bg-teal-900/30' : 'border-teal-200 bg-teal-50'}`}>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-teal-300' : 'text-teal-800'}`}>
                            📝 Decoder Note
                          </h4>
                          <p className={`text-sm ${isDarkMode ? 'text-teal-400/70' : 'text-teal-600'}`}>Show a note at bottom of decoder page (visible on all tabs)</p>
                        </div>
                        <button
                          onClick={async () => {
                            const newVal = !decoderSettings.disclaimer_visible;
                            setDecoderSettings(prev => ({...prev, disclaimer_visible: newVal}));
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({...decoderSettings, disclaimer_visible: newVal})
                              });
                              showNotification(newVal ? 'Note visible' : 'Note hidden', 'success');
                            } catch (e) { showNotification('Failed to update', 'error'); }
                          }}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${decoderSettings.disclaimer_visible ? 'bg-teal-500' : 'bg-gray-300'}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${decoderSettings.disclaimer_visible ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                      {decoderSettings.disclaimer_visible && (
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>Note Text</label>
                          <textarea
                            rows={3}
                            value={decoderSettings.disclaimer_text || ''}
                            onChange={(e) => setDecoderSettings(prev => ({...prev, disclaimer_text: e.target.value}))}
                            placeholder="Enter note text shown at bottom of decoder page for all users..."
                            className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-teal-900/50 border-teal-700 text-white placeholder-teal-500' : 'bg-white border-teal-300'}`}
                          />
                          <button
                            onClick={async () => {
                              try {
                                await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify(decoderSettings)
                                });
                                showNotification('Note saved', 'success');
                              } catch (e) { showNotification('Failed to save', 'error'); }
                            }}
                            className="mt-2 px-4 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm"
                          >
                            Save Note
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Tab Customization */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-indigo-800 bg-indigo-900/30' : 'border-indigo-200 bg-indigo-50'}`}>
                    <h4 className={`font-semibold flex items-center gap-2 mb-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-800'}`}>
                      🏷️ Tab Icons & Labels
                    </h4>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-indigo-400/70' : 'text-indigo-600'}`}>Customize the 3 user-facing tabs (My Activity, My Words, How It Works)</p>
                    <div className="space-y-3">
                      {[
                        { key: 'tab_activity', defaultLabel: 'My Activity', defaultIcon: '📋' },
                        { key: 'tab_vocabulary', defaultLabel: 'My Words', defaultIcon: '📚' },
                        { key: 'tab_help', defaultLabel: 'How It Works', defaultIcon: '📖' },
                      ].map(tab => (
                        <div key={tab.key} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}>
                          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{tab.defaultIcon} {tab.defaultLabel}</p>
                          <div className="flex items-center gap-3">
                            {/* Icon preview + pick */}
                            <div className="flex items-center gap-1">
                              {decoderSettings[`${tab.key}_icon_url`] ? (
                                <>
                                  <img src={`${API.replace('/api', '')}${decoderSettings[`${tab.key}_icon_url`]}`} alt="" className="w-8 h-8 object-contain rounded" />
                                  <button onClick={async () => {
                                    setDecoderSettings(prev => ({...prev, [`${tab.key}_icon_url`]: ''}));
                                    try {
                                      await fetch(`${API}/admin/decoder/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ [`${tab.key}_icon_url`]: '' }) });
                                      showNotification('Tab icon cleared', 'success');
                                    } catch {}
                                  }} className="text-red-400 text-xs hover:text-red-300">✕</button>
                                </>
                              ) : <span className="text-2xl w-8 text-center">{tab.defaultIcon}</span>}
                              <button onClick={() => openIconPicker(tab.key)} className={`px-2 py-1 rounded text-xs font-medium ${isDarkMode ? 'bg-indigo-700 hover:bg-indigo-600 text-white' : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'}`}>
                                Pick
                              </button>
                            </div>
                            {/* Label input */}
                            <input
                              type="text"
                              placeholder={tab.defaultLabel}
                              value={decoderSettings[`${tab.key}_label`] || ''}
                              onChange={(e) => setDecoderSettings(prev => ({...prev, [`${tab.key}_label`]: e.target.value}))}
                              className={`flex-1 px-3 py-1.5 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                            />
                            <button
                              onClick={async () => {
                                try {
                                  await fetch(`${API}/admin/decoder/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ [`${tab.key}_label`]: decoderSettings[`${tab.key}_label`] || '' }) });
                                  showNotification('Tab label saved', 'success');
                                } catch { showNotification('Failed to save', 'error'); }
                              }}
                              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-xs"
                            >Save</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  </>)}

                  {/* DeepSeek AI Sub-Tab */}
                  {decoderSubTab === 'deepseek' && (<>
                  {/* API Key Section */}
                  <div className={`p-4 rounded-lg border-2 mb-4 ${isDarkMode ? 'bg-gradient-to-r from-blue-900/30 to-cyan-900/30 border-blue-500' : 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🔑</span>
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>DeepSeek API Key (Decoder)</h3>
                        {decoderDsKey.set ? (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">Configured</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">Not Set</span>
                        )}
                      </div>
                      <button
                        onClick={() => setDecoderDsKey(prev => ({...prev, showInput: !prev.showInput}))}
                        className={`px-3 py-1 text-sm rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'}`}
                      >{decoderDsKey.showInput ? 'Close' : decoderDsKey.set ? 'Change Key' : 'Add Key'}</button>
                    </div>
                    <p className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Separate API key for decoder. Falls back to translation DeepSeek key if not set.
                    </p>
                    <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                      Get your key from <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">platform.deepseek.com/api_keys</a>
                    </p>
                    {decoderDsKey.set && !decoderDsKey.showInput && (
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current: <span className="font-mono">{decoderDsKey.masked}</span></p>
                    )}
                    {decoderDsKey.showInput && (
                      <div className="mt-3 space-y-3">
                        {decoderDsKey.set && (
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Current: <span className="font-mono">{decoderDsKey.masked}</span></p>
                        )}
                        <div className="flex gap-2">
                          <input type="password" value={decoderDsKey.newKey}
                            onChange={(e) => setDecoderDsKey(prev => ({...prev, newKey: e.target.value}))}
                            placeholder="Enter DeepSeek API key (sk-...)"
                            className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                          <button disabled={!decoderDsKey.newKey.trim() || decoderDsKey.saving}
                            onClick={async () => {
                              setDecoderDsKey(prev => ({...prev, saving: true}));
                              try {
                                const res = await fetch(`${API}/admin/decoder/settings`, {
                                  method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({ deepseek_api_key: decoderDsKey.newKey.trim() })
                                });
                                if (res.ok) {
                                  setDecoderDsKey({ set: true, masked: `***${decoderDsKey.newKey.trim().slice(-4)}`, showInput: false, newKey: '', saving: false });
                                  showNotification('DeepSeek API key saved', 'success');
                                } else { throw new Error(); }
                              } catch(e) {
                                setDecoderDsKey(prev => ({...prev, saving: false}));
                                showNotification('Failed to save key', 'error');
                              }
                            }}
                            className={`px-4 py-2 rounded-lg font-medium ${!decoderDsKey.newKey.trim() || decoderDsKey.saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white'}`}
                          >{decoderDsKey.saving ? 'Saving...' : 'Save Key'}</button>
                        </div>
                        {decoderDsKey.set && (
                          <button onClick={async () => {
                            if (!window.confirm('Remove Decoder DeepSeek Key? AI features will stop working until a new key is added.')) return;
                            try {
                              await fetch(`${API}/admin/decoder/settings`, {
                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify({ deepseek_api_key: '' })
                              });
                              setDecoderDsKey({ set: false, masked: '', showInput: false, newKey: '', saving: false });
                              showNotification('DeepSeek key removed', 'success');
                            } catch(e) { showNotification('Failed to remove key', 'error'); }
                          }} className="w-full px-3 py-2 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200 font-medium">
                            Remove API Key
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Prompt Settings */}
                  <div className={`p-4 rounded-lg border-2 mb-4 ${isDarkMode ? 'bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">⚙️</span>
                        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Prompt Settings (Teaching)</h3>
                      </div>
                      <button onClick={() => setDecoderDsPrompt(prev => ({...prev, showEditor: !prev.showEditor}))}
                        className={`px-3 py-1 text-sm rounded-lg ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white hover:bg-gray-100 text-gray-800'}`}
                      >{decoderDsPrompt.showEditor ? 'Collapse' : 'Edit Prompt'}</button>
                    </div>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Teach DeepSeek how to decode Darija — persona, grammar rules, and example breakdowns. JSON output format is locked.
                    </p>

                    {decoderDsPrompt.showEditor && (
                      <div className="mt-4 space-y-4">
                        {/* Temperature */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Temperature: {decoderDsPrompt.temperature}
                          </label>
                          <input type="range" min="0" max="1" step="0.05" value={decoderDsPrompt.temperature}
                            onChange={(e) => setDecoderDsPrompt(prev => ({...prev, temperature: parseFloat(e.target.value)}))}
                            className="w-full"
                          />
                          <div className="flex justify-between text-xs text-gray-500"><span>0 = Precise</span><span>1 = Creative</span></div>
                        </div>

                        {/* Persona */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>AI Persona</label>
                          <textarea rows={4} value={decoderDsPrompt.persona}
                            onChange={(e) => setDecoderDsPrompt(prev => ({...prev, persona: e.target.value}))}
                            placeholder="Who is the AI? e.g. You are a Moroccan Darija expert..."
                            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                        </div>

                        {/* Grammar Rules */}
                        <div>
                          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Grammar Rules</label>
                          <textarea rows={6} value={decoderDsPrompt.grammar}
                            onChange={(e) => setDecoderDsPrompt(prev => ({...prev, grammar: e.target.value}))}
                            placeholder="Darija grammar reference for the AI..."
                            className={`w-full px-3 py-2 border rounded-lg text-sm font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                          />
                        </div>

                        {/* Examples */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            Example Decodes ({decoderDsPrompt.examples.length})
                          </label>
                          {decoderDsPrompt.examples.map((ex, idx) => (
                            <div key={idx} className={`p-3 rounded-lg mb-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                              <div className="flex justify-between items-start mb-2">
                                <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Example {idx + 1}</span>
                                <button onClick={() => setDecoderDsPrompt(prev => ({...prev, examples: prev.examples.filter((_, i) => i !== idx)}))}
                                  className="text-red-500 hover:text-red-700 text-xs">Remove</button>
                              </div>
                              <input type="text" value={ex.input || ''} placeholder="Darija input (e.g. bghit nakol)"
                                onChange={(e) => {
                                  const updated = [...decoderDsPrompt.examples];
                                  updated[idx] = {...updated[idx], input: e.target.value};
                                  setDecoderDsPrompt(prev => ({...prev, examples: updated}));
                                }}
                                className={`w-full px-2 py-1 border rounded text-sm mb-1 ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                              />
                              <input type="text" value={ex.output?.full_meaning || ''} placeholder="Full meaning (e.g. I want to eat)"
                                onChange={(e) => {
                                  const updated = [...decoderDsPrompt.examples];
                                  updated[idx] = {...updated[idx], output: {...(updated[idx].output || {}), full_meaning: e.target.value}};
                                  setDecoderDsPrompt(prev => ({...prev, examples: updated}));
                                }}
                                className={`w-full px-2 py-1 border rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                              />
                              {ex.output?.words && (
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  Words: {ex.output.words.map(w => `${w.word} = ${w.meaning}`).join(', ')}
                                </p>
                              )}
                            </div>
                          ))}
                          <button onClick={() => setDecoderDsPrompt(prev => ({...prev, examples: [...prev.examples, {input: '', output: {full_meaning: '', words: []}}]}))}
                            className={`text-sm px-3 py-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >+ Add Example</button>
                        </div>

                        {/* JSON Format Notice */}
                        <div className={`p-3 rounded-lg text-sm ${isDarkMode ? 'bg-blue-900/30 border border-blue-700 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
                          The JSON output format (word-by-word breakdown structure) is locked and cannot be changed. Only persona, grammar, and examples are customizable.
                        </div>

                        {/* Save & Reset */}
                        <div className="flex gap-2">
                          <button disabled={decoderDsPrompt.saving}
                            onClick={async () => {
                              setDecoderDsPrompt(prev => ({...prev, saving: true}));
                              try {
                                const res = await fetch(`${API}/admin/decoder/deepseek-prompt`, {
                                  method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                  body: JSON.stringify({
                                    ds_persona: decoderDsPrompt.persona,
                                    ds_grammar: decoderDsPrompt.grammar,
                                    ds_examples: decoderDsPrompt.examples,
                                    ds_temperature: decoderDsPrompt.temperature
                                  })
                                });
                                if (res.ok) {
                                  showNotification('DeepSeek prompt settings saved', 'success');
                                } else { throw new Error(); }
                              } catch(e) { showNotification('Failed to save prompt settings', 'error'); }
                              setDecoderDsPrompt(prev => ({...prev, saving: false}));
                            }}
                            className={`px-4 py-2 rounded-lg font-medium text-sm ${decoderDsPrompt.saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'}`}
                          >{decoderDsPrompt.saving ? 'Saving...' : 'Save Prompt Settings'}</button>
                          <button onClick={() => {
                            if (!window.confirm('Reset to default prompt settings?')) return;
                            setDecoderDsPrompt(prev => ({
                              ...prev,
                              persona: decoderSettings.ds_persona_default || '',
                              grammar: decoderSettings.ds_grammar_default || '',
                              examples: decoderSettings.ds_examples_default || [],
                              temperature: 0.2
                            }));
                            showNotification('Reset to defaults — click Save to apply', 'info');
                          }}
                            className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                          >Reset to Defaults</button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Test Section */}
                  <div className={`p-4 rounded-lg border-2 ${isDarkMode ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">🧪</span>
                      <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Test DeepSeek Decoder</h3>
                    </div>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Test a Darija phrase through DeepSeek (works regardless of active provider — compare with Groq).
                    </p>
                    <div className="flex gap-2 mb-3">
                      <input type="text" value={decoderDsTest.text}
                        onChange={(e) => setDecoderDsTest(prev => ({...prev, text: e.target.value}))}
                        placeholder="Enter Darija text (e.g. bghit nakol, fin ghadi, labas 3lik)"
                        className={`flex-1 px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                        onKeyDown={(e) => { if (e.key === 'Enter' && decoderDsTest.text.trim() && !decoderDsTest.loading) { e.target.closest('div').querySelector('button').click(); }}}
                      />
                      <button disabled={!decoderDsTest.text.trim() || decoderDsTest.loading || !decoderDsKey.set}
                        onClick={async () => {
                          setDecoderDsTest(prev => ({...prev, loading: true, result: null}));
                          try {
                            const res = await fetch(`${API}/admin/decoder/deepseek-test`, {
                              method: 'POST', headers: {...authHeaders, 'Content-Type': 'application/json'},
                              body: JSON.stringify({ text: decoderDsTest.text.trim() })
                            });
                            const data = await res.json();
                            if (res.ok) {
                              setDecoderDsTest(prev => ({...prev, loading: false, result: data}));
                            } else {
                              setDecoderDsTest(prev => ({...prev, loading: false, result: { error: data.detail || 'Test failed' }}));
                            }
                          } catch(e) {
                            setDecoderDsTest(prev => ({...prev, loading: false, result: { error: 'Network error' }}));
                          }
                        }}
                        className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap ${
                          !decoderDsTest.text.trim() || decoderDsTest.loading || !decoderDsKey.set
                            ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                            : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                        }`}
                      >{decoderDsTest.loading ? 'Testing...' : 'Test Decode'}</button>
                    </div>
                    {!decoderDsKey.set && (
                      <p className={`text-sm ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>Add a DeepSeek API key above to enable testing.</p>
                    )}
                    {decoderDsTest.result && (
                      <div className={`mt-3 rounded-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                        {decoderDsTest.result.error ? (
                          <div className="p-3 text-red-500 text-sm">{decoderDsTest.result.error}</div>
                        ) : (<>
                          {/* Parsed result */}
                          {decoderDsTest.result.parsed && (
                            <div className="p-3 space-y-2">
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                {decoderDsTest.result.parsed.full_meaning}
                              </p>
                              {decoderDsTest.result.parsed.words && (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                                      <th className="text-left py-1 px-2">Word</th>
                                      <th className="text-left py-1 px-2">Latin</th>
                                      <th className="text-left py-1 px-2">Arabic</th>
                                      <th className="text-left py-1 px-2">Meaning</th>
                                      <th className="text-left py-1 px-2">Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {decoderDsTest.result.parsed.words.map((w, i) => (
                                      <tr key={i} className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                                        <td className="py-1 px-2 font-medium">{w.word}</td>
                                        <td className="py-1 px-2">{w.latin}</td>
                                        <td className="py-1 px-2 font-arabic">{w.arabic}</td>
                                        <td className="py-1 px-2">{w.meaning}</td>
                                        <td className="py-1 px-2"><span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{w.type}</span></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                          {/* Tokens */}
                          {decoderDsTest.result.tokens && (
                            <div className={`px-3 py-2 border-t ${isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'} text-xs flex gap-4`}>
                              <span>Input tokens: {decoderDsTest.result.tokens.input}</span>
                              <span>Output tokens: {decoderDsTest.result.tokens.output}</span>
                            </div>
                          )}
                          {/* Raw JSON */}
                          <details className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                            <summary className={`px-3 py-2 text-xs cursor-pointer ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}>
                              Raw JSON Response
                            </summary>
                            <pre className={`px-3 py-2 text-xs overflow-x-auto max-h-60 ${isDarkMode ? 'text-gray-300 bg-gray-900' : 'text-gray-700 bg-gray-50'}`}>
                              {decoderDsTest.result.raw}
                            </pre>
                          </details>
                        </>)}
                      </div>
                    )}
                  </div>
                  </>)}

                  {/* Reports Sub-Tab */}
                  {decoderSubTab === 'reports' && (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📨 User Reports ({decoderReports.filter(r => r.status === 'pending').length} pending)
                    </h3>
                    {decoderReports.filter(r => r.status === 'pending').length === 0 ? (
                      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <p>No pending reports. Users haven't reported any issues yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {decoderReports.filter(r => r.status === 'pending').map((report) => (
                          <div key={report.id || report._id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  Reported text: <span className="text-blue-500">"{report.text}"</span>
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  User message: {report.message || 'No message'}
                                </p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                  User: {report.user_email || 'No email'} ({report.user_id ? report.user_id.substring(0, 8) + '...' : 'Unknown'}) | {new Date(report.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            {/* Correction form */}
                            <div className="mt-3 space-y-2">
                              <textarea
                                placeholder="Write correction note for the user..."
                                value={decoderCorrectionText}
                                onChange={(e) => setDecoderCorrectionText(e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                                rows={2}
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={async () => {
                                    if (!decoderCorrectionText.trim()) { showNotification('Write a correction note', 'error'); return; }
                                    try {
                                      const res = await fetch(`${API}/admin/decoder/reports/${report.id || report._id}/correct`, {
                                        method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                        body: JSON.stringify({ correction_note: decoderCorrectionText })
                                      });
                                      if (res.ok) {
                                        showNotification('Correction sent to user!', 'success');
                                        setDecoderCorrectionText('');
                                        loadData();
                                      }
                                    } catch (e) { showNotification('Failed to send correction', 'error'); }
                                  }}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                >
                                  ✅ Send Correction
                                </button>
                                <button
                                  onClick={async () => {
                                    try {
                                      await fetch(`${API}/admin/decoder/reports/${report.id || report._id}`, {
                                        method: 'DELETE', headers: authHeaders
                                      });
                                      showNotification('Report dismissed', 'success');
                                      loadData();
                                    } catch (e) { showNotification('Failed to dismiss', 'error'); }
                                  }}
                                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  )}

                  {/* Usage Stats Sub-Tab */}
                  {decoderSubTab === 'usage' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Decoder Usage Statistics</h4>
                        <button onClick={fetchDecoderUsageStats} disabled={decoderUsageLoading}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>
                          {decoderUsageLoading ? 'Loading...' : 'Refresh'}
                        </button>
                      </div>

                      {/* Monthly quota card */}
                      <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Groq API Usage This Month</span>
                          <span className={`text-sm font-bold ${
                            decoderUsageStats.total_month > decoderUsageStats.groq_limit * 0.8
                              ? 'text-red-500'
                              : decoderUsageStats.total_month > decoderUsageStats.groq_limit * 0.5
                                ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600')
                                : (isDarkMode ? 'text-green-400' : 'text-green-600')
                          }`}>
                            {decoderUsageStats.total_month} / {decoderUsageStats.groq_limit}
                          </span>
                        </div>
                        {/* Progress bar */}
                        <div className={`w-full h-3 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                          <div
                            className={`h-3 rounded-full transition-all ${
                              decoderUsageStats.total_month > decoderUsageStats.groq_limit * 0.8 ? 'bg-red-500' :
                              decoderUsageStats.total_month > decoderUsageStats.groq_limit * 0.5 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, (decoderUsageStats.total_month / decoderUsageStats.groq_limit) * 100)}%` }}
                          />
                        </div>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {Math.round((decoderUsageStats.total_month / decoderUsageStats.groq_limit) * 100)}% of monthly quota used
                        </p>
                        {decoderUsageStats.total_month > decoderUsageStats.groq_limit * 0.8 && (
                          <p className="text-xs mt-1 text-red-500 font-medium">Warning: Approaching Groq monthly limit!</p>
                        )}
                      </div>

                      {/* Daily usage chart */}
                      <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                        <h5 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Daily Decodes (Last 30 Days)</h5>
                        {decoderUsageStats.daily.length > 0 ? (
                          <div className="flex items-end gap-1" style={{ height: '120px' }}>
                            {(() => {
                              const maxCount = Math.max(...decoderUsageStats.daily.map(d => d.count), 1);
                              return decoderUsageStats.daily.map((day, i) => {
                                const pct = (day.count / maxCount) * 100;
                                const dailyLimit = decoderSettings.rate_limit_per_day || 30;
                                const color = day.count > dailyLimit * 0.8 ? (isDarkMode ? '#ef4444' : '#dc2626') :
                                              day.count > dailyLimit * 0.5 ? (isDarkMode ? '#eab308' : '#ca8a04') :
                                              (isDarkMode ? '#22c55e' : '#16a34a');
                                return (
                                  <div key={i} className="flex-1 flex flex-col items-center group relative" style={{ minWidth: '8px' }}>
                                    <div className={`absolute bottom-full mb-1 px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 z-10 pointer-events-none ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-800 text-white'}`}>
                                      {day.date}: {day.count} decodes, {day.unique_users} users
                                    </div>
                                    <div
                                      className="w-full rounded-t"
                                      style={{ height: `${Math.max(pct, 2)}%`, backgroundColor: color, minHeight: '2px' }}
                                    />
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        ) : (
                          <p className={`text-sm text-center py-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No data yet. Click Refresh to load.</p>
                        )}
                        <div className="flex justify-between mt-1">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {decoderUsageStats.daily.length > 0 ? decoderUsageStats.daily[0].date : ''}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {decoderUsageStats.daily.length > 0 ? decoderUsageStats.daily[decoderUsageStats.daily.length - 1].date : ''}
                          </span>
                        </div>
                      </div>

                      {/* Top users table */}
                      <div className={`p-4 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                        <h5 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Top Users This Month</h5>
                        {decoderUsageStats.top_users.length > 0 ? (
                          <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className={isDarkMode ? 'bg-gray-600' : 'bg-gray-100'}>
                                  <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>#</th>
                                  <th className={`text-left px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Email</th>
                                  <th className={`text-right px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Decodes</th>
                                  <th className={`text-right px-3 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Last Active</th>
                                </tr>
                              </thead>
                              <tbody>
                                {decoderUsageStats.top_users.map((u, i) => (
                                  <tr key={u.user_id} className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                                    <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{i + 1}</td>
                                    <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{u.email}</td>
                                    <td className={`px-3 py-2 text-right font-medium ${
                                      u.count > 100 ? 'text-red-500' : u.count > 50 ? (isDarkMode ? 'text-yellow-400' : 'text-yellow-600') : (isDarkMode ? 'text-gray-200' : 'text-gray-800')
                                    }`}>{u.count}</td>
                                    <td className={`px-3 py-2 text-right text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {u.last_used ? new Date(u.last_used).toLocaleDateString() : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No usage data yet</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Search History Sub-Tab */}
                  {decoderSubTab === 'history' && (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                        🔍 Search History ({decoderHistoryTotal})
                      </h4>
                      {decoderSearchHistory.length > 0 && (
                        <button
                          onClick={async () => {
                            if (!window.confirm('Delete ALL search history? This cannot be undone.')) return;
                            try {
                              await fetch(`${API}/admin/decoder/history`, { method: 'DELETE', headers: authHeaders });
                              showNotification('All history deleted', 'success');
                              fetchDecoderHistory(1, '');
                              setDecoderSearchFilter('');
                            } catch (e) { showNotification('Failed to delete', 'error'); }
                          }}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs"
                        >
                          🗑️ Delete All
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      placeholder="Search by user ID, email, or text..."
                      value={decoderSearchFilter}
                      onChange={(e) => { setDecoderSearchFilter(e.target.value); fetchDecoderHistory(1, e.target.value); }}
                      className={`w-full px-3 py-2 mb-4 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                    />
                    {decoderSearchHistory.length === 0 ? (
                      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <p>No search history found.</p>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
                                <th className="text-left py-2 px-3">User ID</th>
                                <th className="text-left py-2 px-3">Email</th>
                                <th className="text-left py-2 px-3">Text Searched</th>
                                <th className="text-left py-2 px-3">Source</th>
                                <th className="text-left py-2 px-3">Date</th>
                                <th className="text-right py-2 px-3">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {decoderSearchHistory.map((h, idx) => (
                                <tr key={h.id || idx} className={`border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                                  <td className={`py-2 px-3 font-mono text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{(h.user_id || '').substring(0, 12)}...</td>
                                  <td className={`py-2 px-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{h.user_email || '-'}</td>
                                  <td className={`py-2 px-3 font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>"{h.text}"</td>
                                  <td className="py-2 px-3">
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      h.source === 'cache' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                                    }`}>
                                      {h.source === 'cache' ? 'Cache' : 'AI'}
                                    </span>
                                  </td>
                                  <td className={`py-2 px-3 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{new Date(h.created_at).toLocaleString()}</td>
                                  <td className="py-2 px-3 text-right">
                                    <button
                                      onClick={async () => {
                                        try {
                                          await fetch(`${API}/admin/decoder/history/${h.id}`, { method: 'DELETE', headers: authHeaders });
                                          showNotification('Entry deleted', 'success');
                                          fetchDecoderHistory(decoderHistoryPage, decoderSearchFilter);
                                        } catch (e) { showNotification('Failed', 'error'); }
                                      }}
                                      className="text-red-500 hover:text-red-700 text-xs"
                                      title="Delete this entry"
                                    >
                                      🗑️
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {decoderHistoryPages > 1 && (
                          <div className="flex items-center justify-between mt-4">
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Page {decoderHistoryPage} of {decoderHistoryPages} ({decoderHistoryTotal} total)
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => fetchDecoderHistory(decoderHistoryPage - 1, decoderSearchFilter)}
                                disabled={decoderHistoryPage <= 1}
                                className={`px-3 py-1 rounded text-xs ${decoderHistoryPage <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-200'} ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => fetchDecoderHistory(decoderHistoryPage + 1, decoderSearchFilter)}
                                disabled={decoderHistoryPage >= decoderHistoryPages}
                                className={`px-3 py-1 rounded text-xs ${decoderHistoryPage >= decoderHistoryPages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-200'} ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  )}

                  {/* Knowledge Base Sub-Tab */}
                  {decoderSubTab === 'knowledge' && (
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      🧠 Knowledge Base ({decoderKnowledgeVerified + decoderKnowledgeUnverified} entries — {decoderKnowledgeVerified} verified, {decoderKnowledgeUnverified} unverified)
                    </h4>
                    <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Verified entries are served instantly to users. Edit wrong entries so users get correct answers next time.
                    </p>
                    {/* Status filter tabs */}
                    <div className="flex gap-2 mb-3">
                      {[
                        { key: 'all', label: 'All', count: decoderKnowledgeVerified + decoderKnowledgeUnverified },
                        { key: 'unverified', label: 'Unverified', count: decoderKnowledgeUnverified },
                        { key: 'verified', label: 'Verified', count: decoderKnowledgeVerified }
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => { setDecoderKnowledgeStatusFilter(tab.key); fetchDecoderKnowledge(1, tab.key, decoderKnowledgeFilter); }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            decoderKnowledgeStatusFilter === tab.key
                              ? tab.key === 'unverified' ? 'bg-yellow-500 text-white' : tab.key === 'verified' ? 'bg-green-600 text-white' : (isDarkMode ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white')
                              : isDarkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {tab.label} ({tab.count})
                        </button>
                      ))}
                    </div>
                    <input
                      type="text"
                      placeholder="Search by text or meaning..."
                      value={decoderKnowledgeFilter}
                      onChange={(e) => { setDecoderKnowledgeFilter(e.target.value); fetchDecoderKnowledge(1, decoderKnowledgeStatusFilter, e.target.value); }}
                      className={`w-full px-3 py-2 mb-4 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                    />
                    {decoderKnowledge.length === 0 ? (
                      <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <p>{decoderKnowledgeStatusFilter === 'unverified' ? 'No unverified entries.' : decoderKnowledgeStatusFilter === 'verified' ? 'No verified entries yet.' : 'No knowledge base entries yet.'}</p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3">
                          {decoderKnowledge.map((entry) => (
                              <div key={entry.id || entry._id} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                        "{entry.text}"
                                      </p>
                                      {entry.is_verified ? (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Verified</span>
                                      ) : (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Unverified</span>
                                      )}
                                    </div>
                                    <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                      Meaning: {entry.result?.full_meaning || 'N/A'}
                                    </p>
                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      Updated: {new Date(entry.updated_at || entry.created_at).toLocaleString()}
                                    </p>
                                  </div>
                                  <div className="flex gap-2 ml-3">
                                    {!entry.is_verified && (
                                      <button
                                        onClick={async () => {
                                          const entryId = entry.id || entry._id;
                                          if (!entryId) { showNotification('Entry has no ID', 'error'); return; }
                                          try {
                                            const res = await fetch(`${API}/admin/decoder/knowledge/${entryId}`, {
                                              method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                              body: JSON.stringify({ is_verified: true })
                                            });
                                            if (res.ok) {
                                              showNotification('Entry verified!', 'success');
                                              fetchDecoderKnowledge(decoderKnowledgePage, decoderKnowledgeStatusFilter, decoderKnowledgeFilter);
                                            } else {
                                              const err = await res.json().catch(() => ({}));
                                              showNotification(err.detail || 'Failed to verify', 'error');
                                            }
                                          } catch (e) { showNotification('Failed to verify: ' + e.message, 'error'); }
                                        }}
                                        className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs"
                                        title="Mark as verified (AI got it right)"
                                      >
                                        Verify
                                      </button>
                                    )}
                                    <button
                                      onClick={() => setEditingKnowledgeEntry(editingKnowledgeEntry?.id === (entry.id || entry._id) ? null : {
                                        id: entry.id || entry._id,
                                        full_meaning: entry.result?.full_meaning || '',
                                        grammar_note: entry.result?.grammar_note || '',
                                        context_note: entry.result?.context_note || '',
                                        words: entry.result?.words || []
                                      })}
                                      className={`px-3 py-1 rounded-lg text-xs ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'} text-white`}
                                      title="Edit word breakdown"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={async () => {
                                        if (!window.confirm('Delete this entry? Next search will trigger a fresh AI call.')) return;
                                        try {
                                          await fetch(`${API}/admin/decoder/knowledge/${entry.id || entry._id}`, {
                                            method: 'DELETE', headers: authHeaders
                                          });
                                          showNotification('Entry deleted', 'success');
                                          fetchDecoderKnowledge(decoderKnowledgePage, decoderKnowledgeStatusFilter, decoderKnowledgeFilter);
                                        } catch (e) { showNotification('Failed to delete', 'error'); }
                                      }}
                                      className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs"
                                      title="Delete (next search will use fresh AI)"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>

                                {/* Word breakdown display */}
                                {entry.result?.words && entry.result.words.length > 0 && editingKnowledgeEntry?.id !== (entry.id || entry._id) && (
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {entry.result.words.map((w, wi) => (
                                      <span key={wi} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                                        <strong>{w.word}</strong> → {w.meaning} <span className="opacity-60">({w.type})</span>
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Edit Form */}
                                {editingKnowledgeEntry?.id === (entry.id || entry._id) && (
                                  <div className={`mt-3 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-900 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                                    <p className={`font-medium text-sm mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Edit Entry</p>
                                    <div className="space-y-3">
                                      <div>
                                        <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full Meaning</label>
                                        <input
                                          type="text"
                                          value={editingKnowledgeEntry.full_meaning}
                                          onChange={(e) => setEditingKnowledgeEntry(prev => ({...prev, full_meaning: e.target.value}))}
                                          className={`w-full px-3 py-2 border rounded-lg text-sm mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                        />
                                      </div>
                                      <div>
                                        <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Words (edit meaning, latin, arabic for each)</label>
                                        <div className="space-y-2 mt-1">
                                          {editingKnowledgeEntry.words.map((w, wi) => (
                                            <div key={wi} className={`flex flex-wrap gap-2 p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                                              <span className={`text-xs font-bold self-center min-w-[60px] ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{w.word}</span>
                                              <input
                                                type="text" placeholder="Meaning"
                                                value={w.meaning || ''}
                                                onChange={(e) => {
                                                  const updated = [...editingKnowledgeEntry.words];
                                                  updated[wi] = {...updated[wi], meaning: e.target.value};
                                                  setEditingKnowledgeEntry(prev => ({...prev, words: updated}));
                                                }}
                                                className={`flex-1 min-w-[100px] px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                              />
                                              <input
                                                type="text" placeholder="Latin"
                                                value={w.latin || ''}
                                                onChange={(e) => {
                                                  const updated = [...editingKnowledgeEntry.words];
                                                  updated[wi] = {...updated[wi], latin: e.target.value};
                                                  setEditingKnowledgeEntry(prev => ({...prev, words: updated}));
                                                }}
                                                className={`w-24 px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                              />
                                              <input
                                                type="text" placeholder="Arabic"
                                                value={w.arabic || ''}
                                                onChange={(e) => {
                                                  const updated = [...editingKnowledgeEntry.words];
                                                  updated[wi] = {...updated[wi], arabic: e.target.value};
                                                  setEditingKnowledgeEntry(prev => ({...prev, words: updated}));
                                                }}
                                                className={`w-24 px-2 py-1 border rounded text-xs ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                      <div className="flex gap-2">
                                        <div className="flex-1">
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Grammar Note</label>
                                          <input
                                            type="text"
                                            value={editingKnowledgeEntry.grammar_note}
                                            onChange={(e) => setEditingKnowledgeEntry(prev => ({...prev, grammar_note: e.target.value}))}
                                            className={`w-full px-3 py-2 border rounded-lg text-sm mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                          />
                                        </div>
                                        <div className="flex-1">
                                          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Context Note</label>
                                          <input
                                            type="text"
                                            value={editingKnowledgeEntry.context_note}
                                            onChange={(e) => setEditingKnowledgeEntry(prev => ({...prev, context_note: e.target.value}))}
                                            className={`w-full px-3 py-2 border rounded-lg text-sm mt-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'}`}
                                          />
                                        </div>
                                      </div>
                                      <div className="flex gap-2 pt-2">
                                        <button
                                          onClick={async () => {
                                            try {
                                              const res = await fetch(`${API}/admin/decoder/knowledge/${editingKnowledgeEntry.id}`, {
                                                method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                                body: JSON.stringify({
                                                  words: editingKnowledgeEntry.words,
                                                  full_meaning: editingKnowledgeEntry.full_meaning,
                                                  grammar_note: editingKnowledgeEntry.grammar_note,
                                                  context_note: editingKnowledgeEntry.context_note,
                                                  is_verified: true
                                                })
                                              });
                                              if (res.ok) {
                                                showNotification('Entry updated & verified!', 'success');
                                                setEditingKnowledgeEntry(null);
                                                fetchDecoderKnowledge(decoderKnowledgePage, decoderKnowledgeStatusFilter, decoderKnowledgeFilter);
                                              } else {
                                                const err = await res.json().catch(() => ({}));
                                                showNotification(err.detail || 'Failed to update', 'error');
                                              }
                                            } catch (e) { showNotification('Failed to update', 'error'); }
                                          }}
                                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                                        >
                                          Save & Verify
                                        </button>
                                        <button
                                          onClick={() => setEditingKnowledgeEntry(null)}
                                          className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                        {decoderKnowledgePages > 1 && (
                          <div className="flex items-center justify-between mt-4">
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Page {decoderKnowledgePage} of {decoderKnowledgePages} ({decoderKnowledgeTotal} entries)
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => fetchDecoderKnowledge(decoderKnowledgePage - 1, decoderKnowledgeStatusFilter, decoderKnowledgeFilter)}
                                disabled={decoderKnowledgePage <= 1}
                                className={`px-3 py-1 rounded text-xs ${decoderKnowledgePage <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-200'} ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                              >
                                Previous
                              </button>
                              <button
                                onClick={() => fetchDecoderKnowledge(decoderKnowledgePage + 1, decoderKnowledgeStatusFilter, decoderKnowledgeFilter)}
                                disabled={decoderKnowledgePage >= decoderKnowledgePages}
                                className={`px-3 py-1 rounded text-xs ${decoderKnowledgePage >= decoderKnowledgePages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-200'} ${isDarkMode ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  )}

                  {decoderSubTab === 'help' && (
                  <div className="space-y-4">
                  {/* Help Content (How It Works) CRUD */}
                  <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
                    <h4 className={`font-semibold flex items-center gap-2 mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                      📖 Help Content (How It Works)
                    </h4>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Manage the "How It Works" tab content that users see in the Decoder page.
                    </p>

                    {/* Add New Section Form */}
                    <div className={`p-4 rounded-lg border mb-4 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                      <h5 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {editingHelpSection ? '✏️ Edit Section' : '➕ Add New Section'}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <input
                          type="text"
                          placeholder="Section title..."
                          value={editingHelpSection ? editingHelpSection.title : newHelpSection.title}
                          onChange={(e) => editingHelpSection
                            ? setEditingHelpSection({...editingHelpSection, title: e.target.value})
                            : setNewHelpSection({...newHelpSection, title: e.target.value})
                          }
                          className={`px-3 py-2 border rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                        />
                        <div className="flex gap-2">
                          <div className="flex items-center gap-1">
                            {(editingHelpSection ? editingHelpSection.icon_url : newHelpSection.icon_url) ? (
                              <>
                                <img src={`${API.replace('/api', '')}${editingHelpSection ? editingHelpSection.icon_url : newHelpSection.icon_url}`} alt="" className="w-8 h-8 object-contain rounded" />
                                <button onClick={() => editingHelpSection
                                  ? setEditingHelpSection({...editingHelpSection, icon_url: ''})
                                  : setNewHelpSection({...newHelpSection, icon_url: ''})
                                } className="text-red-400 text-xs hover:text-red-300">✕</button>
                              </>
                            ) : (
                              <input
                                type="text"
                                placeholder="Icon"
                                value={editingHelpSection ? editingHelpSection.icon : newHelpSection.icon}
                                onChange={(e) => editingHelpSection
                                  ? setEditingHelpSection({...editingHelpSection, icon: e.target.value})
                                  : setNewHelpSection({...newHelpSection, icon: e.target.value})
                                }
                                className={`w-16 px-2 py-2 border rounded-lg text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                              />
                            )}
                            <button onClick={() => openIconPicker(editingHelpSection ? `help_${editingHelpSection.id}` : 'help_new')} className={`px-2 py-1.5 rounded text-xs font-medium ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>
                              Pick
                            </button>
                          </div>
                          <input
                            type="number" min="0" max="99"
                            placeholder="Order"
                            value={editingHelpSection ? editingHelpSection.order : newHelpSection.order}
                            onChange={(e) => editingHelpSection
                              ? setEditingHelpSection({...editingHelpSection, order: parseInt(e.target.value) || 0})
                              : setNewHelpSection({...newHelpSection, order: parseInt(e.target.value) || 0})
                            }
                            className={`w-20 px-3 py-2 border rounded-lg text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'border-gray-300'}`}
                          />
                          <label className="flex items-center gap-1 text-sm">
                            <input
                              type="checkbox"
                              checked={editingHelpSection ? editingHelpSection.is_active : newHelpSection.is_active}
                              onChange={(e) => editingHelpSection
                                ? setEditingHelpSection({...editingHelpSection, is_active: e.target.checked})
                                : setNewHelpSection({...newHelpSection, is_active: e.target.checked})
                              }
                            />
                            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Active</span>
                          </label>
                        </div>
                      </div>
                      <textarea
                        rows={3}
                        placeholder="Section content... (Explain how the feature works — never say AI, use 'our system' or 'our experts')"
                        value={editingHelpSection ? editingHelpSection.content : newHelpSection.content}
                        onChange={(e) => editingHelpSection
                          ? setEditingHelpSection({...editingHelpSection, content: e.target.value})
                          : setNewHelpSection({...newHelpSection, content: e.target.value})
                        }
                        className={`w-full px-3 py-2 border rounded-lg text-sm mb-3 ${isDarkMode ? 'bg-gray-700 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300'}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={async () => {
                            const section = editingHelpSection || newHelpSection;
                            if (!section.title.trim() || !section.content.trim()) {
                              showNotification('Title and content are required', 'error'); return;
                            }
                            try {
                              const url = editingHelpSection
                                ? `${API}/admin/decoder/help/${editingHelpSection.id}`
                                : `${API}/admin/decoder/help`;
                              const method = editingHelpSection ? 'PUT' : 'POST';
                              const res = await fetch(url, {
                                method, headers: {...authHeaders, 'Content-Type': 'application/json'},
                                body: JSON.stringify(section)
                              });
                              if (res.ok) {
                                showNotification(editingHelpSection ? 'Section updated!' : 'Section added!', 'success');
                                setEditingHelpSection(null);
                                setNewHelpSection({ title: '', content: '', icon: '📝', icon_url: '', order: 0, is_active: true });
                                // Reload help sections
                                const helpRes = await fetch(`${API}/admin/decoder/help`, { headers: authHeaders });
                                if (helpRes.ok) { const d = await helpRes.json(); setDecoderHelpSections(d.sections || d || []); }
                              } else { showNotification('Failed to save section', 'error'); }
                            } catch (e) { showNotification('Failed to save', 'error'); }
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                        >
                          {editingHelpSection ? '💾 Update Section' : '➕ Add Section'}
                        </button>
                        {editingHelpSection && (
                          <button
                            onClick={() => setEditingHelpSection(null)}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 text-sm"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Existing Help Sections List */}
                    {decoderHelpSections.length === 0 ? (
                      <p className={`text-sm text-center py-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        No help sections yet. They will be auto-seeded when the server starts, or add one above.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {[...decoderHelpSections].sort((a, b) => (a.order || 0) - (b.order || 0)).map((section) => (
                          <div key={section.id} className={`flex items-center justify-between p-3 rounded-lg border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'} ${!section.is_active ? 'opacity-50' : ''}`}>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {section.icon_url
                                ? <img src={`${API.replace('/api', '')}${section.icon_url}`} alt="" className="w-6 h-6 object-contain" />
                                : <span className="text-xl">{section.icon}</span>}
                              <div className="min-w-0 flex-1">
                                <p className={`font-medium text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                  {section.title}
                                  {!section.is_active && <span className="ml-2 text-xs text-red-400">(hidden)</span>}
                                </p>
                                <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {section.content.substring(0, 80)}...
                                </p>
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-500 text-gray-300' : 'bg-gray-100 text-gray-500'}`}>
                                #{section.order}
                              </span>
                            </div>
                            <div className="flex gap-1 ml-3">
                              <button
                                onClick={() => openIconPicker(`help_edit_${section.id}`)}
                                className="px-2 py-1 text-purple-500 hover:bg-purple-50 rounded text-sm"
                                title="Change Icon"
                              >
                                🎨
                              </button>
                              <button
                                onClick={() => setEditingHelpSection({...section, icon_url: section.icon_url || ''})}
                                className="px-2 py-1 text-blue-500 hover:bg-blue-50 rounded text-sm"
                                title="Edit"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Delete "${section.title}"?`)) return;
                                  try {
                                    await fetch(`${API}/admin/decoder/help/${section.id}`, {
                                      method: 'DELETE', headers: authHeaders
                                    });
                                    showNotification('Section deleted', 'success');
                                    setDecoderHelpSections(prev => prev.filter(s => s.id !== section.id));
                                  } catch (e) { showNotification('Failed to delete', 'error'); }
                                }}
                                className="px-2 py-1 text-red-500 hover:bg-red-50 rounded text-sm"
                                title="Delete"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  </div>
                  )}

                </div>

      {/* Icon Picker — uses shared IconPicker (browses ALL Image Manager folders) */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={async (url) => {
            setIconPickerOpen(false);
            if (iconPickerTarget === 'page') {
              setDecoderSettings(prev => ({...prev, decoder_icon_url: url}));
              try { await fetch(`${API}/admin/decoder/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ decoder_icon_url: url }) }); showNotification('Decoder icon saved!', 'success'); } catch {}
            } else if (iconPickerTarget.startsWith('tab_')) {
              const field = `${iconPickerTarget}_icon_url`;
              setDecoderSettings(prev => ({...prev, [field]: url}));
              try { await fetch(`${API}/admin/decoder/settings`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ [field]: url }) }); showNotification('Tab icon saved!', 'success'); } catch {}
            } else if (iconPickerTarget === 'help_new') {
              setNewHelpSection(prev => ({...prev, icon_url: url}));
              showNotification('Icon selected — save the section to apply', 'info');
            } else if (iconPickerTarget.startsWith('help_edit_')) {
              const sectionId = iconPickerTarget.replace('help_edit_', '');
              try {
                await fetch(`${API}/admin/decoder/help/${sectionId}`, { method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'}, body: JSON.stringify({ icon_url: url }) });
                setDecoderHelpSections(prev => prev.map(s => s.id === sectionId ? {...s, icon_url: url} : s));
                showNotification('Section icon saved!', 'success');
              } catch { showNotification('Failed to save icon', 'error'); }
            } else if (iconPickerTarget.startsWith('help_')) {
              const sectionId = iconPickerTarget.replace('help_', '');
              if (editingHelpSection && editingHelpSection.id === sectionId) {
                setEditingHelpSection(prev => ({...prev, icon_url: url}));
              }
              showNotification('Icon selected — save the section to apply', 'info');
            }
          }}
        />
      )}
    </>
  );

};

export default DecoderAdmin;
